/**
 * Guide engine v3 - narration audio (#1026, epic #1020). Spec R9.
 *
 * Plays a step's pre-generated Polly narration mp3 for the active voice gender +
 * locale. Files live under `<cdnBase>/<packageKey>audio/<locale>/<file>` (the
 * versioned packageKey gives automatic cache-busting). One stream at a time.
 *
 * Browsers block autoplay without a user gesture: the first-login auto-started
 * intro will not autoplay step 1 until the user interacts; the replay control is
 * the escape hatch. A blocked play() is swallowed. Default voice is female (R9);
 * the on/off + gender choice persists via voicePrefs.js. Clean-room, no v1 reuse.
 */

import { guideCdnBaseUrl } from '../loader';
import { DEFAULT_LOCALE } from '../contract';

/**
 * Resolve a step's narration url, or null when nothing should play (no audio,
 * voice off, missing file, or CDN unconfigured).
 * @param {{audio?: {female?: string, male?: string}}} step
 * @param {{voiceGender: string, packageKey: string, locale?: string}} opts
 * @returns {string|null}
 */
export const resolveAudioUrl = (step, { voiceGender, packageKey, locale = DEFAULT_LOCALE } = {}) => {
  if (!step || !step.audio) return null;
  if (!voiceGender || voiceGender === 'off') return null;
  if (typeof packageKey !== 'string' || packageKey.length === 0) return null;
  const file = step.audio[voiceGender];
  if (typeof file !== 'string' || file.length === 0) return null;
  let base;
  try {
    base = guideCdnBaseUrl();
  } catch {
    return null;
  }
  return `${base}/${packageKey.replace(/^\/+/, '')}audio/${locale}/${file}`;
};

/**
 * Create a single-stream audio controller. Starting a new step (or replay) stops
 * whatever is playing first.
 */
export const createAudioController = () => {
  let current = null;

  const stop = () => {
    if (current) {
      current.pause();
      current.src = '';
      current = null;
    }
  };

  /** Play a step's narration. No-op when voice is off / no audio / CDN unconfigured. */
  const play = (step, opts) => {
    stop();
    const url = resolveAudioUrl(step, opts || {});
    if (!url) return;
    const audio = new Audio(url);
    current = audio;
    // Autoplay may be blocked without a prior user gesture; that is expected and
    // the replay control lets the user start it deliberately. Never throw, but
    // log the reason: silently-failing playback is undiagnosable (#1042).
    const started = audio.play();
    if (started && typeof started.catch === 'function') {
      started.catch((err) => {
        console.warn('guide narration play() failed:', err && err.name, err && err.message);
      });
    }
  };

  return {
    play, replay: play, stop, resolveAudioUrl, isPlaying: () => current != null,
  };
};
